import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getExistProp } from '../utils/common.utils';

type PaginationType = {
  page?: number;
  limit?: number;
  keyword?: string;
  urbanCenter?: number | null;
  zone?: number | null;
  productType?: number | null;
  tableType?: number | null;
  platform?: string | null;
  /* eslint-disable-next-line */
} & { [key: string]: any };

type UseQueryParamsOptions = {
  disablePagination?: boolean;
};

export default function useQueryParams<T>(
  options: UseQueryParamsOptions = {},
  // eslint-disable-next-line
): [query: T & PaginationType, onParamChange: Function] {
  const { disablePagination = false } = options;
  const prevKeyword = useRef('');
  const [searchParams, setSearchParams] = useSearchParams();
  /* eslint-disable-next-line */
  const onParamChange = (param: any) => {
    if (prevKeyword.current !== param.keyword) {
      prevKeyword.current = param.keyword;
    }
    // reset to first page if the users input new keyword
    if (param.keyword && param.keyword !== prevKeyword && !disablePagination) {
      param.page = 1;
    }
    setSearchParams(
      getExistProp({
        ...query,
        ...param,
      }),
    );
  };
  const query = useMemo(() => {
    /* eslint-disable-next-line */
    const result: { [key: string]: any } = Object.fromEntries([...searchParams]);
    if (!disablePagination) {
      if (result.page) {
        result.page = parseInt(result.page);
      } else {
        result.page = 1;
      }
      if (result.limit) {
        result.limit = parseInt(result.limit);
      } else {
        result.limit = 100;
      }
    }
    if (result.productType) {
      result.productType = Number(result.productType);
    } else {
      result.productType = null;
    }
    if (result.urbanCenter) {
      result.urbanCenter = Number(result.urbanCenter);
    } else {
      result.urbanCenter = null;
    }
    if (result.zone) {
      result.zone = Number(result.zone);
    } else {
      result.zone = null;
    }
    if (result.tableType) {
      result.tableType = Number(result.tableType);
    } else {
      result.tableType = null;
    }
    result.platform = result.platform ? result.platform : null;
    return result;
  }, [searchParams, disablePagination]) as T & PaginationType;

  return [query, onParamChange];
}
